import React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { ButtonV2, DialogV2, Flex, LinkV2, TextV2, Toggle } from '@withjoy/joykit';
import { Copy, Link as LinkIcon } from '@withjoy/joykit/icons';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { ShareDialogProps } from './ShareDialog.types';
import { useShareDialogController } from './ShareDialog.controller';
import { UnpublishedWebsiteWarning } from '@apps/admin/routes/WebsiteDesignerV2/components/UnpublishedWebsiteWarning/UnpublishedWebsiteWarning';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';

type FieldProps = Readonly<{
  label: string;
  editUrl?: string;
  onEditClick?: () => void;
  value: string;
}>;

const Field: React.FC<FieldProps> = ({ label, editUrl, onEditClick, value, children }) => {
  const { tq } = useTranslation('shareDialog');

  return (
    <Flex
      flexDirection="column"
      gap={5}
    >
      <Flex
        flexDirection="column"
        gap={3}
      >
        <Flex
          alignItems="center"
          gap={3}
        >
          <TextV2 typographyVariant="label3">{label}</TextV2>
          {editUrl && (
            <LinkV2
              href={editUrl}
              onClick={onEditClick}
              typographyVariant="label3"
              textDecoration="underline"
              textDecorationColor="#D9D9D9"
              fontWeight={600}
              color="mono14"
              style={{
                textUnderlineOffset: pxToRem(2),
              }}
              _hover={{
                textDecoration: 'none',
                color: 'mono14',
              }}
              _visited={{
                color: 'mono14',
              }}
              _active={{
                color: 'mono14',
              }}
            >
              {tq`Edit`}
            </LinkV2>
          )}
        </Flex>
        <Flex
          typographyVariant="body2"
          paddingX={5}
          paddingY={4}
          backgroundColor="mono2"
          borderRadius={2}
          border="1px solid"
          borderColor="mono5"
          color="mono8"
        >
          {value}
        </Flex>
      </Flex>
      <Flex
        gap={5}
        flexWrap="wrap"
      >
        {children}
      </Flex>
    </Flex>
  );
};

const Action: React.FC<{ onClick?: () => void }> = ({ onClick, children }) => {
  return (
    <Flex
      as="button"
      role="button"
      flexShrink={0}
      alignItems="center"
      gap={2}
      typographyVariant="label4"
      fontWeight={500}
      color="newUIBrandPrimary"
      textDecoration="underline"
      textDecorationStyle="dotted"
      textDecorationColor="#4B51E6"
      cursor="pointer"
      style={{
        textUnderlineOffset: pxToRem(4),
      }}
      _hover={{
        textDecoration: 'none',
      }}
      onClick={onClick}
    >
      {children}
    </Flex>
  );
};

export const ShareDialog: React.FC<ShareDialogProps> = props => {
  const { isOpen, isGuest } = props;
  const { shareOptions, guestShareOptions, onOpenNewTab, onCloseDialogHandle, unpublishedWebsiteWarningProps, publishFormik, showPublishSection } = useShareDialogController(props);
  const { tq } = useTranslation('shareDialog');

  const options = isGuest ? guestShareOptions : shareOptions;
  const joyUrlOption = options[0];
  const passwordOption = !isGuest ? options[1] : null;

  return (
    <>
      <ResponsiveDialog
        isOpen={isOpen}
        onClose={onCloseDialogHandle}
        hideDivider
        hasCloseButton={false}
        hideFooterDivider
        removeBodyPadding
        dialogSize="md"
        drawerOverrides={{
          ...drawerOverrides,
          Body: { props: { gap: 6, paddingY: [8, 8, 8], paddingX: [6, 6, 6] } },
        }}
        dialogOverrides={{
          Content: { props: { width: ['100%', pxToRem(540)], gap: 6, padding: [9, 9, 9] } },
          Body: { props: { paddingX: [0, 0, 0], paddingY: [0, 0, 0] } },
        }}
      >
        <DialogV2.CloseButton
          paddingX={3}
          paddingY={3}
          top={0}
          right={0}
          width="auto"
          height="auto"
        />
        <TextV2
          typographyVariant="hed6"
          fontWeight={600}
          marginBottom={7}
          width="100%"
          textAlign="center"
        >
          {isGuest ? tq`Share this event.` : tq`Share with your guests.`}
        </TextV2>
        <Flex
          flexDirection="column"
          gap={6}
        >
          <Field
            label={tq`Joy URL`}
            editUrl={joyUrlOption.editLink}
            onEditClick={joyUrlOption.onEditLinkClick}
            value={joyUrlOption.text}
          >
            <CopyToClipboard text={joyUrlOption.text}>
              <Action onClick={joyUrlOption.onCopy}>
                <Copy size="sm" />
                {tq`Copy Event URL`}
              </Action>
            </CopyToClipboard>
            <Action onClick={onOpenNewTab}>
              <LinkIcon size="sm" />
              {tq`Open in a New Tab`}
            </Action>
          </Field>

          {passwordOption && (
            <Field
              label={tq`Event Password`}
              editUrl={passwordOption.editLink}
              onEditClick={passwordOption.onEditLinkClick}
              value={passwordOption.text}
            >
              <CopyToClipboard text={passwordOption.text}>
                <Action onClick={passwordOption.onCopy}>
                  <Copy size="sm" />
                  {tq`Copy Event Password`}
                </Action>
              </CopyToClipboard>
            </Field>
          )}

          {showPublishSection && !isGuest && (
            <Flex
              flexDirection="column"
              gap={6}
              paddingTop={3}
            >
              <TextV2
                typographyVariant="hed3"
                fontWeight={600}
              >
                {tq`Publish your site before sharing.`}
              </TextV2>
              <Flex
                flexDirection="column"
                gap={3}
              >
                <Flex
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={3}
                >
                  <TextV2 typographyVariant="body2">{tq`Publish your website`}</TextV2>
                  <Toggle
                    {...publishFormik.getFieldProps('published')}
                    label=""
                    hideLabel={true}
                    checked={publishFormik.values.published}
                    intent="neutral"
                  />
                </Flex>
                <TextV2
                  typographyVariant="label2"
                  color="mono8"
                >
                  {tq`Keep your site unpublished while you're building it. Publish it when you're ready for guests to visit.`}
                </TextV2>
              </Flex>
              <Flex
                justifyContent="center"
                width="100%"
                paddingTop={5}
              >
                <ButtonV2
                  size="lg"
                  intent="newPrimary"
                  shape="rounded"
                  onClick={() => publishFormik.handleSubmit()}
                  width="100%"
                  maxWidth={pxToRem(380)}
                  loading={publishFormik.isSubmitting}
                  disabled={!publishFormik.dirty}
                >
                  {tq`Save`}
                </ButtonV2>
              </Flex>
            </Flex>
          )}
        </Flex>
      </ResponsiveDialog>
      <UnpublishedWebsiteWarning {...unpublishedWebsiteWarningProps} />
    </>
  );
};
